import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {message} from 'antd';
import {AxiosError, AxiosResponse} from 'axios';
import type FILTERS from '../../../common/filters.type';
import {ADD_VEHICLE, VEHICLE_DATA, UPDATE_VEHICLE} from './vehicle.type';
import vehicleServices from './vehicleService';

const initialState = {
  isLoading: false,
  vehicles: {
    page_size: 10,
    page_number: 1,
    sort_by: 'name',
    ascending: true,
    search: '',
    is_active: undefined as undefined | boolean,
    totalDataCount: 0,
    pageData: [] as VEHICLE_DATA[],
  },
};

export const vehiclesList = createAsyncThunk('vehiclesList', async (payload: FILTERS, thunkAPI) => {
  try {
    const data: AxiosResponse = await vehicleServices.vehiclesList(payload);

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});

export const addVehicle = createAsyncThunk('addVehicle', async (values: ADD_VEHICLE, thunkAPI) => {
  try {
    const data: AxiosResponse = await vehicleServices.addVehicle(values);

    return data;
  } catch (error) {
    const result = error as AxiosError;

    return thunkAPI.rejectWithValue(result.message);
  }
});
export const updateVehicle = createAsyncThunk(
  'updateVehicle',
  async (
    values: {
      payload: UPDATE_VEHICLE;
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      const data: AxiosResponse = await vehicleServices.updateVehicle({
        payload: values.payload,
        id: values.id,
      });

      return data;
    } catch (error) {
      const result = error as AxiosError;

      return thunkAPI.rejectWithValue(result.message);
    }
  },
);

export const vehiclesSlice = createSlice({
  name: 'vehicles',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    pageNumberChange(state, action: PayloadAction<number>) {
      state.vehicles.page_number = action.payload;
    },
    pageSizeChange(state, action: PayloadAction<number>) {
      state.vehicles.page_number = 1;
      state.vehicles.page_size = action.payload;
    },
    searchBy(state, action: PayloadAction<string>) {
      state.vehicles.page_number = 1;
      state.vehicles.search = action.payload;
    },
    sortBy(state, action: PayloadAction<{ascending: boolean; sortBy: string}>) {
      state.vehicles.ascending = action.payload.ascending;
      state.vehicles.sort_by = action.payload.sortBy;
    },
    vehiclesStatusFilter(state, action: PayloadAction<boolean | undefined>) {
      state.vehicles.page_number = 1;
      state.vehicles.is_active = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(vehiclesList.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(vehiclesList.fulfilled, (state, action) => {
      state.isLoading = false;
      state.vehicles = action.payload.data;
    });
    builder.addCase(vehiclesList.rejected, (state, action) => {
      state.isLoading = false;
      state.vehicles = initialState.vehicles;
      message.error(`${action.payload}`);
    });

    builder.addCase(addVehicle.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addVehicle.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Vehicle added Successfully`);
    });
    builder.addCase(addVehicle.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });

    builder.addCase(updateVehicle.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateVehicle.fulfilled, (state) => {
      state.isLoading = false;
      message.success(`Vehicle updated Successfully`);
    });
    builder.addCase(updateVehicle.rejected, (state, action) => {
      state.isLoading = false;
      message.error(`${action.payload}`);
    });
  },
});
export const {pageNumberChange, pageSizeChange, searchBy, sortBy, vehiclesStatusFilter} =
  vehiclesSlice.actions;

export default vehiclesSlice.reducer;
